<script lang="ts" setup>
import { api } from '@/api';
import { mdiStepBackward } from '@mdi/js';
import { ref, computed } from 'vue';

import Dialog from '@/components/Dialog/Dialog.vue';

import {
  DataTypeInfoDto,
  EvaluationParameterDto,
  NumericEvaluationParameterDto,
} from '@/api/generator';

import EvaluationsManualTasksModalScreen01 from './EvaluationsManualTasksModalScreen01.vue';
import EvaluationsManualTasksModalScreen02 from './EvaluationsManualTasksModalScreen02.vue';
import { checkLimits } from '../utils';

const selectedItem = ref<DataTypeInfoDto>();
const numericEvaluationParameterDto = ref<NumericEvaluationParameterDto>();
const selectedSlide = ref(0);
const isFormValid = ref(true);

const overlayOpen = defineModel<boolean>('overlayOpen', {
  required: true,
});

const emit = defineEmits<{
  add: [param: EvaluationParameterDto];
}>();

async function nextHandler() {
  if (!selectedItem.value) return;

  const { dataTypeCategory, id } = selectedItem.value;
  const isQuantity = dataTypeCategory === 'Quantity';
  const isEnumeration = dataTypeCategory === 'Enumeration';

  if (isQuantity) {
    if (selectedSlide.value === 0) {
      selectedSlide.value = 1;
      return;
    }
    if (selectedSlide.value === 1 && numericEvaluationParameterDto.value) {
      emit('add', numericEvaluationParameterDto.value);
      overlayOpen.value = false;
    }
  } else if (isEnumeration) {
    try {
      const { result } = await api.taskItemsApi.getEnumerationEvaluationParameterForManualTask({
        enumerationId: id || '',
      });

      if (result) {
        emit('add', result);
        overlayOpen.value = false;
      }
    } catch (error) {
      console.error('Error fetching enumeration evaluation parameter:', error);
    }
  }
}

const afterLeaveHandler = () => {
  selectedItem.value = undefined;
  selectedSlide.value = 0;
};

const nextAddButtonEnabled = computed<boolean>(() => {
  if (!selectedItem.value) return false;

  if (selectedSlide.value === 1) {
    const dto = numericEvaluationParameterDto.value;
    if (
      !dto?.definition ||
      !dto?.limitType ||
      !dto?.evaluationType ||
      !checkLimits(dto.min, dto.max) ||
      !isFormValid.value
    ) {
      return false;
    }
  }

  return true;
});
</script>

<template>
  <Dialog
    v-model="overlayOpen"
    size="large"
    data-testid="dialog_select_or_configure_evaluation_quantity_or_enumeration"
    @afterLeave="afterLeaveHandler"
  >
    <template #title>{{
      selectedSlide === 0
        ? $t('SelectEvaluationQuantityOrEnumeration')
        : $t('ConfigureEvaluationQuantity')
    }}</template>

    <template #text>
      <v-window v-model="selectedSlide" hideDelimiters :cycle="false" :showArrows="false">
        <v-window-item minHeight="1000px">
          <EvaluationsManualTasksModalScreen01 v-model:selectedItem="selectedItem" />
        </v-window-item>
        <v-window-item>
          <EvaluationsManualTasksModalScreen02
            :dataTypeInfoDto="selectedItem || undefined"
            :slideActive="selectedSlide === 1"
            @changeNumericEvaluationParameterDto="
              (value: any) => {
                if (value) {
                  numericEvaluationParameterDto = value;
                }
              }
            "
            @validationStateChange="(valid: boolean) => (isFormValid = valid)"
          />
        </v-window-item>
      </v-window>
    </template>

    <template #actions>
      <TextVBtn :text="$t('Cancel')" @click="() => (overlayOpen = false)" />
      <TextVBtn
        v-if="selectedSlide === 1"
        :text="$t('Back')"
        :prependIcon="mdiStepBackward"
        @click="() => (selectedSlide = 0)"
      />
      <SecondaryVBtn :disabled="!nextAddButtonEnabled" @click="nextHandler">
        {{
          !selectedItem ||
          (selectedItem && selectedItem.dataTypeCategory === 'Quantity' && selectedSlide === 0)
            ? $t('Next')
            : $t('Add')
        }}
      </SecondaryVBtn>
    </template>
  </Dialog>
</template>
